import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { AppIcon } from '../../ui/AppIcon';
import { AdminApiError, adminFetch } from './api';
import { ComponentRegistry } from './ComponentRegistry';
import { addPasskey, passkeyError, supportsPasskeys } from './passkeys';
import styles from './passkeys.module.css';

interface Passkey { id: string; name: string; deviceType: string; backedUp: boolean; createdAt: string; lastUsedAt: string | null }
export function PasskeyPanel({ onAccessFailure, onBusyChange }: { onAccessFailure: (error: unknown) => void; onBusyChange: (busy: boolean) => void }): ReactElement {
  const [keys, setKeys] = useState<Passkey[] | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState<'add' | Passkey | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const active = useRef(true);
  const loadAbort = useRef<AbortController | null>(null);
  const failure = useCallback((value: unknown) => {
    setError(passkeyError(value));
    if (value instanceof AdminApiError && (value.status === 401 || (value.status === 403 && value.code !== 'PASSKEY_ORIGIN_MISMATCH'))) onAccessFailure(value);
  }, [onAccessFailure]);
  const load = useCallback(async () => {
    loadAbort.current?.abort(); const abort = new AbortController(); loadAbort.current = abort;
    try {
      const result = await adminFetch<{ passkeys: Passkey[] }>('/admin/passkeys', { signal: abort.signal });
      if (active.current && !abort.signal.aborted) setKeys(result.passkeys);
    } catch (error) { if (active.current && !abort.signal.aborted) failure(error); }
  }, [failure]);
  useEffect(() => {
    active.current = true; void load();
    return () => { active.current = false; loadAbort.current?.abort(); };
  }, [load]);
  useEffect(() => { onBusyChange(busy); return () => onBusyChange(false); }, [busy, onBusyChange]);
  async function submit() {
    if (busy || !action) return;
    setBusy(true); setError(''); setNotice('');
    try {
      if (action === 'add') await addPasskey(name.trim(), password, () => active.current);
      else await adminFetch('/auth/passkey/remove', { method: 'POST', body: { id: action.id, password } });
      if (active.current) { setNotice(action === 'add' ? '通行密钥已添加。' : '通行密钥已移除。'); setAction(null); setName(''); await load(); }
    } catch (error) { if (active.current) failure(error); }
    finally { if (active.current) { setPassword(''); setBusy(false); } }
  }
  function choose(next: 'add' | Passkey | null) { setAction(next); setPassword(''); setName(''); setError(''); setNotice(''); }
  return <div className={styles.panel}>
    <div className={styles.heading}><h3>通行密钥</h3><button type="button" disabled={busy || keys === null || !supportsPasskeys() || keys.length >= 10} onClick={() => choose('add')}>添加 Passkey</button></div>
    {!supportsPasskeys() && <p role="status">此浏览器暂不支持创建通行密钥。</p>}
    {action && <ComponentRegistry id="admin.passkeys.form">
      <form className={styles.form} aria-label={action === 'add' ? '添加通行密钥' : '移除通行密钥'} onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <h4>{action === 'add' ? '添加通行密钥' : `移除 ${action.name}`}</h4>
        {action === 'add' && <label>密钥名称<input autoFocus value={name} maxLength={80} required disabled={busy} autoComplete="off" onChange={(event) => setName(event.currentTarget.value)} /></label>}
        <label>当前密码<input type="password" value={password} autoComplete="current-password" required minLength={8} maxLength={128} disabled={busy} onChange={(event) => setPassword(event.currentTarget.value)} /></label>
        <div className={styles.actions}><button type="submit" disabled={busy}>{busy ? '正在验证…' : action === 'add' ? '创建通行密钥' : '确认移除'}</button><button type="button" disabled={busy} onClick={() => choose(null)}>取消</button></div>
      </form>
    </ComponentRegistry>}
    <ComponentRegistry id="admin.passkeys.list">
      {keys === null ? <p role="status">{error ? '无法加载通行密钥。' : '正在加载…'}</p> : keys.length === 0 ? <p>尚未添加通行密钥</p> : <ul className={styles.list}>{keys.map((key) => <li key={key.id}>
        <div><strong>{key.name}</strong><span>{key.backedUp ? '已同步' : '设备密钥'}</span><small>添加于 {new Date(key.createdAt).toLocaleString()}</small><small>最近使用：{key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : '尚未使用'}</small></div>
        <button type="button" className={styles.remove} aria-label={`移除 ${key.name}`} title={`移除 ${key.name}`} disabled={busy} onClick={() => choose(key)}><AppIcon name="delete" size={18} /></button>
      </li>)}</ul>}
    </ComponentRegistry>
    <ComponentRegistry id="admin.passkeys.status">{error && <p role="alert">{error}</p>}{error && keys === null && <button type="button" onClick={() => { setError(''); void load(); }}>重试</button>}{notice && <p role="status">{notice}</p>}</ComponentRegistry>
  </div>;
}
